import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Check, Plus, X } from '../../icons';
import {
  listDelegates, createDelegate, revokeDelegate, extractErrorMessage,
} from '../../api/approval';
import type { ApprovalDelegatesResponse } from '../../types/approval';
import { ApproverPicker, type PickedUser } from './ApproverPicker';
import { Button, FormField, Input } from '../../components/shared';
// 손짠 머리 → 정본 PageHeader · 경로 · 범위 칩(2026-10-17 · web 레인 W6 — lib/page-crumbs.ts)
import { PageHeader } from '../../components/shared';
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import { useAuthStore } from '../../store/authStore';
import '../AttendancePage/AttendanceAdmin.css';

/**
 * 결재 위임 — **부재 중이면 대결자에게 간다** (결재 A5, 2026-08-24).
 *
 * 설계: 제안 4 — *"위임 — `ApprovalDelegate` 표가 이미 있다(기간·사유). 부재 중이면 대결자에게
 * 간다. … 위임이 없으면 휴가철에 결재가 멈춘다 — 그리고 위임은 표가 이미 있으므로 연결부만
 * 만들면 된다."*
 *
 * ★★ 표도 API 도 있었고 **화면만 없었다.** 그래서 위임을 걸 길이 아예 없었다.
 * ★ **해지가 없으면 위임은 한 방향 문**이다 — 잘못 맡겨도 끝날 때까지 기다려야 한다.
 *   그래서 이 화면과 함께 해지(`PATCH`)를 열었다.
 * ★ 지우지 않고 **끈다**. 그 기간에 남이 대신 결재한 사실은 남아야 한다 — 목록에도 지난 위임이
 *   그대로 보인다(회색으로).
 *
 * ── 자리가 둘이다 (2026-08-31 · 시안 F1·F3) ──────────────────────────────
 * 시안은 위임을 **두 자리**에 둔다 — 담당자의 「관리 · 담당자 › 위임」(회사 전체)과
 * 개인의 「개인 설정 › 내 위임」(내가 맡기는 것). **주인이 다르면 자리도 이름도 다르다.**
 * 화면은 하나이고 `scope` 만 갈린다 — 두 벌로 만들면 언젠가 한쪽만 고쳐진다.
 *
 * ★★ **끄는 것은 맡긴 사람만 한다.** 회사 범위에서도 남의 위임에는 「해지」가 안 붙는다 —
 *    서버가 `NOT_RELATED` 로 막기 때문이고(있는 규칙이다), 눌러도 안 되는 단추를 그리면
 *    **할 수 없는 일을 화면이 권하는 것**이 된다.
 * ★ 회사 범위를 물어도 관리자가 아니면 서버가 **조용히 내 것으로 내려 준다**(403 을 안 만든다).
 *   그래서 응답의 `scope` 를 보고 화면이 「무엇을 보고 있는지」 말한다.
 */

function todayIso(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

interface Props {
  /** `company` = 회사 전체(담당자) · `self` = 내가 맡긴 것. 기본은 종전 그대로 `self`. */
  scope?: 'self' | 'company';
}

export function DelegatePage({ scope = 'self' }: Props = {}): JSX.Element {
  const myId = useAuthStore((s) => s.user?.id?.toString() ?? '');
  const [data, setData] = useState<ApprovalDelegatesResponse | null>(null);
  const [picked, setPicked] = useState<PickedUser[]>([]);
  const [startDate, setStartDate] = useState(todayIso);
  const [endDate, setEndDate] = useState(() => todayIso(7));
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await listDelegates(scope === 'company' ? 'company' : undefined));
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { void load(); }, [load]);

  const onCreate = async () => {
    const who = picked[0];
    if (!who) return;
    setBusy(true); setError(null); setDone(null);
    try {
      await createDelegate({
        delegateId: who.userId, startDate, endDate,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      setPicked([]); setReason('');
      setDone(`${who.name} 님에게 결재를 맡겼습니다.`);
      await load();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally { setBusy(false); }
  };

  const onRevoke = async (id: string, name: string | null | undefined) => {
    setBusy(true); setError(null); setDone(null);
    try {
      await revokeDelegate(id);
      setDone(`${name ?? '위임'}을(를) 해지했습니다.`);
      await load();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally { setBusy(false); }
  };

  const outgoing = data?.outgoing ?? [];
  const incoming = data?.incomingActive ?? [];
  const isCompany = scope === 'company';
  /**
   * **정말 회사 전체가 왔나.** 물어본 것과 받은 것이 다를 수 있다 — 관리자가 아니면 서버가
   * 조용히 내 것으로 내려 주고(403 을 안 만든다), 옛 서버는 이 칸 자체를 안 준다.
   * 그래서 «물어본 범위» 가 아니라 **«받은 범위»** 로 화면 글자를 고른다.
   */
  const seesCompany = data?.scope === 'company';
  const canCreate = picked.length === 1 && startDate <= endDate;

  return (
    <div className="hra">
      {/* ★ 제목은 **메뉴 이름과 같다**(`appr-menu.test.ts` 가 못 박는다). */}
      <PageHeader
        breadcrumb={crumbsFor(isCompany ? '/approval/admin/delegates' : '/approval/me/delegates')}
        scope={scopeFor(isCompany ? '/approval/admin/delegates' : '/approval/me/delegates')}
        title={isCompany ? '위임' : '내 위임'}
        description={isCompany
          ? (seesCompany
              ? `지금 회사에 걸린 위임 ${outgoing.length} — 누가 누구에게, 언제까지 맡겼나`
              : '회사 전체는 회사 관리자만 봅니다 — 아래는 내가 맡긴 것입니다.')
          : `자리를 비우는 동안 내 결재를 대신 받을 사람을 정합니다 — 맡긴 위임 ${outgoing.length} · 받은 위임 ${incoming.length}`}
      />

      {error && <p className="hra__error" role="alert"><AlertCircle size={14} aria-hidden />{error}</p>}
      {done && <p className="hra__ok"><Check size={14} aria-hidden />{done}</p>}
      {loading && !data && <p className="hra__empty">불러오는 중…</p>}

      {/* 내가 받은 위임 — 읽기만 한다. 끄는 것은 맡긴 사람의 일이다. */}
      {incoming.length > 0 && (
        <section className="hra__form" aria-label="내가 받은 위임">
          <h2 className="hra__rulehead">내가 대신 결재하는 것 {incoming.length}</h2>
          <div className="hra__rulelist">
            {incoming.map((d) => (
              <div key={`${d.principalId}-${d.startDate}`} className="hra__rule">
                <b>{d.principalName ?? `사용자 ${d.principalId}`}</b> 님의 결재
                <span className="hra__dim"> · {d.startDate} ~ {d.endDate}</span>
              </div>
            ))}
          </div>
          <p className="hra__note">
            이 기간에는 그분 앞으로 온 결재가 <b>내 결재함에도 함께 보입니다</b>.
            해지는 <b>맡긴 분만</b> 할 수 있습니다 — 받은 쪽이 끄면 맡긴 분은 부재 중에 결재가 멈춘 것을 모릅니다.
          </p>
        </section>
      )}

      {/* 내가 맡긴 위임 */}
      <section className="hra__form" aria-label={isCompany ? '회사의 위임' : '내가 맡긴 위임'}>
        <h2 className="hra__rulehead">
          {isCompany && seesCompany ? '회사의 위임' : '내가 맡긴 위임'} {outgoing.length}
        </h2>
        {outgoing.length === 0 ? (
          <p className="hra__empty">
            {isCompany && seesCompany ? '지금 걸린 위임이 없습니다.' : '아직 맡긴 위임이 없습니다.'}
          </p>
        ) : (
          <div className="hra__rulelist">
            {outgoing.map((d) => {
              const live = d.running === true;
              const soon = d.upcoming === true;
              return (
                <div key={d.id} className={`hra__rule${d.isActive ? '' : ' hra__dim'}`}>
                  {isCompany && seesCompany && (
                    <>
                      <b>{d.principalName ?? `사용자 ${d.principalId}`}</b>
                      <span className="hra__dim"> → </span>
                    </>
                  )}
                  <b>{d.delegateName ?? `사용자 ${d.delegateId}`}</b>
                  {d.delegatePosition ? <span className="hra__dim"> · {d.delegatePosition}</span> : null}
                  <span className="hra__dim"> · {d.startDate} ~ {d.endDate}</span>
                  {' '}
                  <span className="hra__dim">
                    {/* D12(2026-10-18) — 결재 상태 «진행 중» 과 다른 뜻(위임 기간)이라 «기간 중» 으로 가른다 */}
                    {!d.isActive ? '· 해지됨' : live ? '· 기간 중' : soon ? '· 예정' : '· 기간 지남'}
                  </span>
                  {d.reason ? <span className="hra__dim"> · {d.reason}</span> : null}
                  {/* 진행 중이거나 예정인 것만 끌 수 있다 — 지난 일을 지금 바꾸는 것처럼 보이면 안 된다. */}
                  {(live || soon) && (d.principalId === myId || !isCompany) && (
                    <Button
                      variant="quiet-danger"
                      size="sm"
                      onClick={() => { void onRevoke(d.id, d.delegateName); }}
                      disabled={busy}
                      iconLeft={<X size={13} aria-hidden />}
                    >
                      해지
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <p className="hra__note">
          해지해도 <b>기록은 지우지 않습니다</b> — 그 기간에 대신 결재한 사실이 남아야 하기 때문입니다.
          기간이 지난 위임은 저절로 도지 않습니다.
          {isCompany && seesCompany && (
            <> 남의 위임은 <b>맡긴 분만 끕니다</b> — 부재 중에 결재가 멈춘 것을 맡긴 분이 모르면 안 됩니다.</>
          )}
        </p>
      </section>

      {/* 새 위임 — 회사 범위에서도 **내 것을 맡기는** 자리다(서버가 남의 위임을 대신 걸어 주지 않는다). */}
      <section className="hra__form" aria-label="새 위임">
        <h2 className="hra__rulehead">{isCompany ? '내 결재 맡기기' : '새 위임'}</h2>
        <ApproverPicker
          label="맡길 사람"
          hint="한 사람만 고릅니다 · 본인은 고를 수 없습니다"
          value={picked}
          onChange={(v) => setPicked(v.slice(-1))}
        />
        <div className="hra__searchrow">
          <FormField className="hra__field" label="시작일">
            <Input
              fullWidth
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                if (e.target.value > endDate) setEndDate(e.target.value);
              }}
            />
          </FormField>
          <FormField className="hra__field" label="종료일">
            <Input fullWidth type="date" value={endDate} min={startDate}
              onChange={(e) => setEndDate(e.target.value)} />
          </FormField>
          <FormField className="hra__field" label="사유">
            <Input
              fullWidth
              type="text"
              value={reason}
              maxLength={200}
              placeholder="연차 · 출장 등"
              onChange={(e) => setReason(e.target.value)}
            />
          </FormField>
          <Button
            onClick={() => { void onCreate(); }}
            disabled={!canCreate || busy}
            iconLeft={<Plus size={14} aria-hidden />}
          >
            맡기기
          </Button>
        </div>
        <p className="hra__note">
          맡긴 기간에는 내 앞으로 온 결재가 <b>그분 결재함에도 보입니다</b>. 내 결재함에서 사라지지는 않습니다 —
          <b> 둘 중 먼저 처리한 쪽</b>이 그 문서를 끝냅니다.
        </p>
      </section>
    </div>
  );
}
